/*
  wishlist: no-app wishlist for the 70 Series Store.
  localStorage first, with an optional logged-in account sync (Phase 2).
  Vanilla JavaScript, no dependencies. Load with defer.

  The wishlist page renders each saved item as the theme's own product card,
  fetched as a bare fragment from /products/{handle}?view=wlcard, so cards match
  collections exactly (image, vendor, title, price, Judge.me rating, Add to cart,
  and the PartBot fitment hooks around .quick-add).
*/
(function () {
  'use strict';

  var STORAGE_KEY = '70s_wishlist_v1';
  var CHANGE_EVENT = 'wishlist:changed';
  var MAX_ITEMS = 100;
  var memoryFallback = null; // used when localStorage is unavailable (private mode)

  /* ----------------------------- storage ----------------------------- */

  function readRaw() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return memoryFallback || [];
    }
  }

  function writeRaw(list) {
    memoryFallback = list;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode or full storage: keep the in-memory copy for this session */
    }
  }

  function normalise(list) {
    var out = [];
    var seen = {};
    if (!Array.isArray(list)) return out;
    for (var i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item) continue;
      var id = parseInt(item.id, 10);
      if (!id || seen[id]) continue;
      seen[id] = true;
      var entry = { id: id, handle: String(item.handle || '') };
      // addedAt (ms) lets a purchase clear only items saved BEFORE it was bought.
      var addedAt = parseInt(item.addedAt, 10);
      if (addedAt > 0) entry.addedAt = addedAt;
      out.push(entry);
    }
    return out;
  }

  function getList() {
    return normalise(readRaw());
  }

  function setList(list) {
    var clean = normalise(list).slice(0, MAX_ITEMS);
    writeRaw(clean);
    emitChange(clean);
    return clean;
  }

  function isSaved(id) {
    id = parseInt(id, 10);
    var list = getList();
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === id) return true;
    }
    return false;
  }

  function toggle(id, handle) {
    id = parseInt(id, 10);
    if (!id) return false;
    var list = getList();
    var next = [];
    var found = false;
    for (var i = 0; i < list.length; i++) {
      if (list[i].id === id) {
        found = true;
      } else {
        next.push(list[i]);
      }
    }
    if (!found) next.push({ id: id, handle: String(handle || ''), addedAt: Date.now() });
    setList(next);
    return !found;
  }

  function emitChange(list) {
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { list: list } }));
  }

  /* ------------------------------ toast ------------------------------ */

  // Small, self-dismissing confirmation ("Added to wishlist" / "Removed from
  // wishlist"). Reuses a single element so rapid clicks just retitle + restart it.
  var toastEl = null;
  var toastHideTimer = null;
  var toastRemoveTimer = null;

  function showToast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
    }
    if (!toastEl.parentNode) document.body.appendChild(toastEl);
    toastEl.textContent = message;

    clearTimeout(toastHideTimer);
    clearTimeout(toastRemoveTimer);
    // Reflow so re-triggering restarts the transition, then show.
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');

    toastHideTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
      toastRemoveTimer = setTimeout(function () {
        if (toastEl && toastEl.parentNode) toastEl.parentNode.removeChild(toastEl);
      }, 300);
    }, 2000);
  }

  /* ----------------------------- buttons ----------------------------- */

  function reflectButton(btn, saved) {
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.classList.toggle('is-saved', !!saved);
    var savedLabel = btn.getAttribute('data-label-saved');
    var unsavedLabel = btn.getAttribute('data-label-unsaved');
    if (savedLabel && unsavedLabel) {
      btn.setAttribute('aria-label', saved ? savedLabel : unsavedLabel);
    }
  }

  function initButtons(root) {
    var scope = root || document;
    var btns = scope.querySelectorAll('[data-wishlist-button]');
    for (var i = 0; i < btns.length; i++) {
      reflectButton(btns[i], isSaved(btns[i].getAttribute('data-product-id')));
    }
  }

  // Remember which wishlist button (if any) a press started on. On desktop the save
  // button sits on top of the card's full-surface product link; if the pointer drifts a
  // hair between press and release, the browser fires the click on their common ancestor
  // (the card link) instead of the button, so it navigates to the product instead of
  // saving - the "hard to click on desktop" problem. Tracking the press target lets us
  // treat the interaction as a save even when the click lands slightly off.
  var pressedWishlistBtn = null;

  function onPointerDown(e) {
    pressedWishlistBtn = e.target.closest ? e.target.closest('[data-wishlist-button]') : null;
  }

  function onDocumentClick(e) {
    var btn = (e.target.closest && e.target.closest('[data-wishlist-button]')) || pressedWishlistBtn;
    pressedWishlistBtn = null;
    if (!btn) return;
    // Capture phase + stopPropagation so the card link never gets this click.
    e.preventDefault();
    e.stopPropagation();
    var id = btn.getAttribute('data-product-id');
    var handle = btn.getAttribute('data-product-handle');
    var saved = toggle(id, handle);
    var all = document.querySelectorAll('[data-wishlist-button][data-product-id="' + id + '"]');
    for (var i = 0; i < all.length; i++) reflectButton(all[i], saved);
    showToast(saved ? 'Added to wishlist' : 'Removed from wishlist');
    if (saved) trackWishlistAdd(id, handle);
  }

  /* -------------------- product page: match button height ------------- */

  // Height comes from CSS (align-self: stretch = Add to cart height), which is immune to
  // re-renders and timing. JS only keeps the button square by setting its width to its
  // own rendered height. It never sets height, so it can never cause a mismatch.
  function matchPdpButtonToAddToCart() {
    var wl = document.querySelector('.product-form__buttons .wishlist-button--pdp');
    if (!wl) return;
    var h = wl.offsetHeight;
    if (h > 0 && wl.style.width !== h + 'px') {
      wl.style.width = h + 'px';
    }
  }

  function initPdpButtonSizing() {
    if (!document.querySelector('.wishlist-button--pdp')) return;
    matchPdpButtonToAddToCart();
    window.addEventListener('load', matchPdpButtonToAddToCart);
    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(matchPdpButtonToAddToCart, 150);
    });
    // Catch late layout / font swaps.
    setTimeout(matchPdpButtonToAddToCart, 500);
    setTimeout(matchPdpButtonToAddToCart, 1500);
    // The theme re-renders the buy buttons on variant change; re-apply on any product
    // area DOM change. MainContent is stable and never replaced, so observe it.
    var host = document.getElementById('MainContent') || document.querySelector('product-info');
    if (host && window.MutationObserver) {
      var mt;
      new MutationObserver(function () {
        clearTimeout(mt);
        mt = setTimeout(matchPdpButtonToAddToCart, 80);
      }).observe(host, { childList: true, subtree: true });
    }
  }

  /* ----------------------------- badge ------------------------------- */

  function updateBadges() {
    var count = getList().length;
    var badges = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < badges.length; i++) {
      badges[i].textContent = count;
      badges[i].hidden = count === 0;
    }
  }

  /* --------------------------- wishlist page -------------------------- */

  function removeButtonMarkup(id) {
    return '<button type="button" class="wishlist-remove" data-wishlist-remove="' + id + '" aria-label="Remove from your list">Remove</button>';
  }

  // Re-wire the card's "Add to cart" (quick-add) buttons. The theme's <modal-opener>
  // binds its click handler in its constructor via this.querySelector('button'). When we
  // inject a card with innerHTML the opener upgrades BEFORE its child button is parsed, so
  // the handler is never attached and Add to cart does nothing (the click then falls
  // through to the card's product link, navigating away). Bind it ourselves here.
  function wireQuickAdd(root) {
    var openers = root.querySelectorAll('modal-opener[data-modal]');
    for (var i = 0; i < openers.length; i++) {
      var opener = openers[i];
      if (opener.getAttribute('data-wl-quickadd-bound')) continue;
      var button = opener.querySelector('button');
      if (!button) continue;
      opener.setAttribute('data-wl-quickadd-bound', '1');
      (function (opener, button) {
        button.addEventListener('click', function (e) {
          var modal = document.querySelector(opener.getAttribute('data-modal'));
          if (modal && typeof modal.show === 'function') {
            e.preventDefault();
            e.stopPropagation();
            modal.show(button);
          }
        });
      })(opener, button);
    }
  }

  function renderPage() {
    var grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    var emptyEl = document.querySelector('[data-wishlist-empty]');
    var list = getList();

    if (!list.length) {
      grid.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    grid.setAttribute('aria-busy', 'true');

    var results = {};
    // Carry a theme preview through to the fragment request so the wishlist page
    // renders correctly while previewing an unpublished theme. Harmless in production.
    var previewParam = '';
    var pv = window.location.search.match(/[?&](preview_theme_id=\d+)/);
    if (pv) previewParam = '&' + pv[1];

    // One request per 20 items (templates/search.wlcards.liquid), not one per item.
    // Anything a batch misses is fetched on its own via the ?view=wlcard fragment.
    function one(item) {
      return fetch('/products/' + encodeURIComponent(item.handle) + '?view=wlcard' + previewParam, { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : null; })
        .catch(function () { return null; })
        .then(function (html) { results[item.id] = html; });
    }
    function batch(items) {
      var q = items.map(function (item) { return item.handle; }).join(',');
      return fetch('/search?view=wlcards&type=product&q=' + encodeURIComponent(q) + previewParam, { headers: { Accept: 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .catch(function () { return ''; })
        .then(function (html) {
          var tpl = document.createElement('template');
          tpl.innerHTML = html;
          var byHandle = {};
          Array.prototype.forEach.call(tpl.content.querySelectorAll('[data-wl-handle]'), function (el) {
            byHandle[el.getAttribute('data-wl-handle')] = el.innerHTML;
          });
          return Promise.all(items.map(function (item) {
            if (byHandle[item.handle]) { results[item.id] = byHandle[item.handle]; return null; }
            return one(item);
          }));
        });
    }
    var jobs = [];
    for (var i = 0; i < list.length; i += 20) jobs.push(batch(list.slice(i, i + 20)));
    Promise.all(jobs).then(function () { paintPage(grid, list, results, emptyEl); });
  }

  function paintPage(grid, list, results, emptyEl) {
    grid.innerHTML = '';
    list.forEach(function (item) {
      var html = results[item.id];
      // Only render items we actually got a card for. A failed or partial fetch is
      // skipped for this render but kept in storage - never auto-wipe the wishlist.
      if (!html || html.indexOf('card-wrapper') === -1) return;
      var li = document.createElement('li');
      li.className = 'wishlist-cell';
      li.setAttribute('data-wishlist-item', item.id);
      li.innerHTML = html;
      // Put Remove INSIDE the card, below Add to cart. The card box (.card) is a flex
      // column (media + content), so appending Remove as its last child stacks it under
      // the content (which ends with Add to cart). This keeps it within the card's
      // border, unlike the card's inner .card__content grid where Add to cart is
      // grid-pinned to the bottom. Falls back to the cell if the card markup changes.
      var card = li.querySelector('.card-wrapper .card') || li.querySelector('.card');
      (card || li).insertAdjacentHTML('beforeend', removeButtonMarkup(item.id));
      grid.appendChild(li);
    });

    grid.setAttribute('aria-busy', 'false');
    if (emptyEl) emptyEl.hidden = getList().length !== 0;
    initButtons(grid);
    wireQuickAdd(grid);
    updateBadges();
  }

  function onGridClick(e) {
    var removeBtn = e.target.closest ? e.target.closest('[data-wishlist-remove]') : null;
    if (!removeBtn) return;
    e.preventDefault();
    e.stopPropagation();
    var id = parseInt(removeBtn.getAttribute('data-wishlist-remove'), 10);
    var next = getList().filter(function (x) { return x.id !== id; });
    setList(next);
    var cell = document.querySelector('[data-wishlist-item="' + id + '"]');
    if (cell && cell.parentNode) cell.parentNode.removeChild(cell);
    showToast('Removed from wishlist');
    if (!next.length) {
      var emptyEl = document.querySelector('[data-wishlist-empty]');
      if (emptyEl) emptyEl.hidden = false;
    }
  }

  function pruneRemovedCells() {
    var grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;
    var saved = {};
    getList().forEach(function (x) { saved[x.id] = true; });
    var cells = grid.querySelectorAll('[data-wishlist-item]');
    for (var i = 0; i < cells.length; i++) {
      if (!saved[parseInt(cells[i].getAttribute('data-wishlist-item'), 10)]) {
        if (cells[i].parentNode) cells[i].parentNode.removeChild(cells[i]);
      }
    }
    if (!getList().length) {
      var emptyEl = document.querySelector('[data-wishlist-empty]');
      if (emptyEl) emptyEl.hidden = false;
    }
  }

  /* ------------------- Klaviyo "Added to Wishlist" ------------------- */

  // Each save is sent to Klaviyo as an "Added to Wishlist" event (it triggers the wishlist
  // flow). Klaviyo drops events from anonymous browsers, so saves wait in a small queue
  // and go out on a later page view once Klaviyo knows who this is (email click, signup
  // form, login). Logged-in customers are identified by the email seeded in
  // wishlist-sync-config.liquid. Queued saves older than 7 days are dropped.
  var KV_QUEUE_KEY = '70s_wishlist_kv_queue_v1';
  var KV_EVENT = 'Added to Wishlist';
  var KV_MAX_AGE = 7 * 24 * 60 * 60 * 1000;
  var kvBusy = false;

  function kvRead() {
    try { return JSON.parse(window.localStorage.getItem(KV_QUEUE_KEY) || '[]') || []; } catch (e) { return []; }
  }
  function kvWrite(q) {
    try { window.localStorage.setItem(KV_QUEUE_KEY, JSON.stringify(q.slice(-10))); } catch (e) {}
  }

  function money(cents) {
    var n = parseInt(cents, 10);
    return n > 0 ? Math.round(n) / 100 : null;
  }

  function trackWishlistAdd(id, handle) {
    if (!handle) return;
    fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (p) {
        if (!p) return;
        var img = p.featured_image || (p.images && p.images[0]) || '';
        if (img && img.indexOf('//') === 0) img = 'https:' + img;
        var props = {
          ProductID: String(p.id || id),
          ProductName: p.title || '',
          Handle: p.handle || handle,
          URL: window.location.origin + '/products/' + (p.handle || handle),
          ImageURL: img,
          Price: money(p.price),
          CompareAtPrice: money(p.compare_at_price),
          OnSale: !!(p.compare_at_price && p.compare_at_price > p.price),
          Brand: p.vendor || '',
          Currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || '',
          WishlistCount: getList().length,
          $value: money(p.price)
        };
        var q = kvRead().filter(function (x) { return x.props && x.props.ProductID !== props.ProductID; });
        q.push({ at: Date.now(), props: props });
        kvWrite(q);
        kvFlush();
      });
  }

  function whenKlaviyo(cb, tries) {
    var k = window.klaviyo;
    if (k && typeof k.isIdentified === 'function') return cb(k);
    if ((tries || 0) > 40) return; // ~10s, then retry on the next page view
    setTimeout(function () { whenKlaviyo(cb, (tries || 0) + 1); }, 250);
  }

  function kvFlush() {
    var now = Date.now();
    var q = kvRead().filter(function (x) { return x && x.props && now - (x.at || 0) < KV_MAX_AGE; });
    kvWrite(q);
    if (!q.length || kvBusy) return;
    kvBusy = true;
    // If Klaviyo never answers (blocked or slow), give up after 8s so the queue is not
    // stuck for the rest of the page view; it retries on the next one.
    var released = false;
    function release() { if (!released) { released = true; kvBusy = false; } }
    setTimeout(release, 8000);
    whenKlaviyo(function (k) {
      Promise.resolve(k.isIdentified())
        .then(function (known) {
          if (released) return;
          var email = window.__wishlistCustomerEmail || '';
          if (!known && !email) return; // anonymous: keep queued
          var ready = known ? Promise.resolve() : Promise.resolve(k.identify({ email: email }));
          return ready.then(function () {
            return q.reduce(function (chain, item) {
              return chain.then(function () { return k.track(KV_EVENT, item.props); });
            }, Promise.resolve());
          }).then(function () {
            var sent = {};
            q.forEach(function (x) { sent[x.props.ProductID + '@' + x.at] = true; });
            kvWrite(kvRead().filter(function (x) { return !sent[x.props.ProductID + '@' + x.at]; }));
          });
        })
        .catch(function () {})
        .then(release);
    });
  }

  /* -------- Phase 2 account sync (guarded, dormant until wired) -------- */

  var syncTimer = null;
  function scheduleSync(list) {
    if (!window.__wishlistCustomerId || !window.__wishlistSyncUrl) return;
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      try {
        // Send as application/x-www-form-urlencoded (a CORS "simple request") so the
        // browser does NOT fire a preflight OPTIONS. Mechanic's webhook endpoint only
        // answers POST (OPTIONS gets a 405), so a preflight would block the whole call.
        // Mechanic's "Auto" data mode parses these params into event.data.
        var params = new URLSearchParams();
        params.set('customerId', window.__wishlistCustomerId);
        params.set('secret', window.__wishlistSyncSecret || '');
        for (var i = 0; i < list.length; i++) {
          params.append('items[]', list[i].id);
        }
        fetch(window.__wishlistSyncUrl, {
          method: 'POST',
          keepalive: true,
          body: params
        });
      } catch (e) {}
    }, 1200);
  }

  /* ----------------------- purchased-item cleanup ---------------------- */

  // window.__wishlistPurchased is { "<product id>": <unix seconds> }, written by the
  // Mechanic task "Wishlist: remove items when they are purchased". Drop any locally
  // saved item that was bought after it was saved (items with no addedAt predate this
  // and count as saved before the purchase). Something re-saved after buying stays.
  function dropPurchased(list) {
    var bought = window.__wishlistPurchased;
    if (!bought || typeof bought !== 'object') return list;
    return list.filter(function (item) {
      var boughtAt = parseInt(bought[item.id], 10);
      if (!boughtAt) return true;
      return (item.addedAt || 0) > boughtAt * 1000;
    });
  }

  /* ------------------------------ init ------------------------------- */

  function init() {
    // Phase 2 hook (dormant until wired): if the logged-in account list was seeded
    // into the page, union it with the local list so devices stay in sync.
    // Purchased items are pruned from the local list first; the server list has
    // already been cleaned by Mechanic, so anything on it is a current save and is
    // stamped as added now so it survives the purchase check on later loads.
    if (Array.isArray(window.__wishlistServerItems)) {
      var local = dropPurchased(getList());
      var known = {};
      local.forEach(function (x) { known[x.id] = true; });
      var now = Date.now();
      var fromServer = normalise(window.__wishlistServerItems)
        .filter(function (x) { return !known[x.id]; })
        .map(function (x) { x.addedAt = now; return x; });
      setList(local.concat(fromServer));
    }
    initButtons(document);
    updateBadges();
    renderPage();
    initPdpButtonSizing();
    kvFlush();
    // A Klaviyo signup form identifies the browser: send queued saves straight away
    window.addEventListener('klaviyoForms', function (e) {
      if (e && e.detail && e.detail.type === 'submit') setTimeout(kvFlush, 1500);
    });

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('click', onDocumentClick, true);
    var grid = document.querySelector('[data-wishlist-grid]');
    if (grid) grid.addEventListener('click', onGridClick);

    document.addEventListener(CHANGE_EVENT, function (e) {
      updateBadges();
      scheduleSync((e.detail && e.detail.list) || getList());
      pruneRemovedCells();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Save several products at once (the Fit-Out Builder's "Save to wish list").
  // Products already saved are left as they are; each new one is tracked like
  // a heart click. Returns how many were added.
  function addMany(items) {
    var list = getList();
    var have = {};
    list.forEach(function (x) { have[x.id] = true; });
    var added = [];
    (items || []).forEach(function (item) {
      var id = parseInt(item && item.id, 10);
      if (!id || have[id]) return;
      have[id] = true;
      list.push({ id: id, handle: String(item.handle || ''), addedAt: Date.now() });
      added.push({ id: id, handle: String(item.handle || '') });
    });
    if (!added.length) return 0;
    setList(list);
    added.forEach(function (item) {
      var all = document.querySelectorAll('[data-wishlist-button][data-product-id="' + item.id + '"]');
      for (var i = 0; i < all.length; i++) reflectButton(all[i], true);
      trackWishlistAdd(item.id, item.handle);
    });
    return added.length;
  }

  // Small public API for the theme and the Phase 2 guest-to-login merge.
  window.Wishlist = { get: getList, set: setList, toggle: toggle, isSaved: isSaved, addMany: addMany, toast: showToast };
})();
